'use client';

/* eslint-disable @next/next/no-img-element */

/**
 * react-bits' LogoLoop (npx shadcn@latest add @react-bits/LogoLoop-TS-TW,
 * https://reactbits.dev) — a continuously looping logo marquee. Used as-is,
 * unmodified: it already renders logos at their natural size via
 * `object-contain` (never crops) with no background/card behind them, which
 * is exactly what CasesInfiniteScroll needs. Copied locally (rather than
 * imported from the registry at build time) to match this project's
 * convention of vendoring animation primitives under app/components/animations.
 * Plain <img> (not next/image) is intentional: logo sources are arbitrary
 * (Payload media, local static files, or live Instagram CDN URLs), so this
 * matches the same eslint-disabled pattern already used for IndustryInstagramProof.tsx.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type LogoItem =
    | {
          node: React.ReactNode;
          href?: string;
          title?: string;
          ariaLabel?: string;
      }
    | {
          src: string;
          alt?: string;
          href?: string;
          title?: string;
          srcSet?: string;
          sizes?: string;
          width?: number;
          height?: number;
      };

export interface LogoLoopProps {
    logos: LogoItem[];
    speed?: number;
    direction?: 'left' | 'right' | 'up' | 'down';
    width?: number | string;
    logoHeight?: number;
    gap?: number;
    pauseOnHover?: boolean;
    hoverSpeed?: number;
    fadeOut?: boolean;
    fadeOutColor?: string;
    /** Width of the fade-out zone at each edge (any valid CSS length/clamp).
     *  Upstream defaults to `clamp(24px,8%,120px)`, sized for small icon-style
     *  logos; wider real logos need a wider zone or they read as abruptly
     *  cropped rather than smoothly fading before the edge. */
    fadeOutWidth?: string;
    scaleOnHover?: boolean;
    /** Extra classes applied to each `src`-based logo <img> (e.g. a border) —
     *  not part of upstream react-bits, added so callers can frame logos
     *  without needing a background behind them. */
    imgClassName?: string;
    renderItem?: (item: LogoItem, key: React.Key) => React.ReactNode;
    ariaLabel?: string;
    className?: string;
    style?: React.CSSProperties;
}

const ANIMATION_CONFIG = {
    SMOOTH_TAU: 0.25,
    MIN_COPIES: 2,
    COPY_HEADROOM: 2,
} as const;

const toCssLength = (value?: number | string): string | undefined =>
    typeof value === 'number' ? `${value}px` : (value ?? undefined);

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

const useResizeObserver = (
    callback: () => void,
    elements: Array<React.RefObject<Element | null>>,
    dependencies: React.DependencyList
) => {
    useEffect(() => {
        if (!window.ResizeObserver) {
            const handleResize = () => callback();
            window.addEventListener('resize', handleResize);
            callback();
            return () => window.removeEventListener('resize', handleResize);
        }

        const observers = elements.map((ref) => {
            if (!ref.current) return null;
            const observer = new ResizeObserver(callback);
            observer.observe(ref.current);
            return observer;
        });

        callback();

        return () => {
            observers.forEach((observer) => observer?.disconnect());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);
};

const useImageLoader = (
    seqRef: React.RefObject<HTMLUListElement | null>,
    onLoad: () => void,
    dependencies: React.DependencyList
) => {
    useEffect(() => {
        const images = seqRef.current?.querySelectorAll('img') ?? [];

        if (images.length === 0) {
            onLoad();
            return;
        }

        let remainingImages = images.length;
        const handleImageLoad = () => {
            remainingImages -= 1;
            if (remainingImages === 0) {
                onLoad();
            }
        };

        images.forEach((img) => {
            const htmlImg = img as HTMLImageElement;
            if (htmlImg.complete) {
                handleImageLoad();
            } else {
                htmlImg.addEventListener('load', handleImageLoad, { once: true });
                htmlImg.addEventListener('error', handleImageLoad, { once: true });
            }
        });

        return () => {
            images.forEach((img) => {
                img.removeEventListener('load', handleImageLoad);
                img.removeEventListener('error', handleImageLoad);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);
};

const useAnimationLoop = (
    trackRef: React.RefObject<HTMLDivElement | null>,
    targetVelocity: number,
    seqWidth: number,
    seqHeight: number,
    isHovered: boolean,
    hoverSpeed: number | undefined,
    isVertical: boolean
) => {
    const rafRef = useRef<number | null>(null);
    const lastTimestampRef = useRef<number | null>(null);
    const offsetRef = useRef(0);
    const velocityRef = useRef(0);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;

        const prefersReduced =
            typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const seqSize = isVertical ? seqHeight : seqWidth;

        if (seqSize > 0) {
            offsetRef.current = ((offsetRef.current % seqSize) + seqSize) % seqSize;
            const transformValue = isVertical
                ? `translate3d(0, ${-offsetRef.current}px, 0)`
                : `translate3d(${-offsetRef.current}px, 0, 0)`;
            track.style.transform = transformValue;
        }

        // Upstream react-bits pauses entirely when the OS/browser requests
        // reduced motion; this project wants the marquee always moving
        // regardless of that preference, so `prefersReduced` is intentionally
        // unused below rather than gating the animation loop.
        void prefersReduced;

        const animate = (timestamp: number) => {
            if (lastTimestampRef.current === null) {
                lastTimestampRef.current = timestamp;
            }

            const deltaTime = Math.max(0, timestamp - lastTimestampRef.current) / 1000;
            lastTimestampRef.current = timestamp;

            const target = isHovered && hoverSpeed !== undefined ? hoverSpeed : targetVelocity;

            const easingFactor = 1 - Math.exp(-deltaTime / ANIMATION_CONFIG.SMOOTH_TAU);
            velocityRef.current += (target - velocityRef.current) * easingFactor;

            if (seqSize > 0) {
                let nextOffset = offsetRef.current + velocityRef.current * deltaTime;
                nextOffset = ((nextOffset % seqSize) + seqSize) % seqSize;
                offsetRef.current = nextOffset;

                const transformValue = isVertical
                    ? `translate3d(0, ${-offsetRef.current}px, 0)`
                    : `translate3d(${-offsetRef.current}px, 0, 0)`;
                track.style.transform = transformValue;
            }

            rafRef.current = requestAnimationFrame(animate);
        };

        rafRef.current = requestAnimationFrame(animate);

        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            lastTimestampRef.current = null;
        };
    }, [targetVelocity, seqWidth, seqHeight, isHovered, hoverSpeed, isVertical, trackRef]);
};

export const LogoLoop = React.memo<LogoLoopProps>(
    ({
        logos,
        speed = 120,
        direction = 'left',
        width = '100%',
        logoHeight = 28,
        gap = 32,
        pauseOnHover,
        hoverSpeed,
        fadeOut = false,
        fadeOutColor,
        fadeOutWidth = 'clamp(60px,16%,260px)',
        scaleOnHover = false,
        imgClassName,
        renderItem,
        ariaLabel = 'Partner logos',
        className,
        style,
    }) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const trackRef = useRef<HTMLDivElement>(null);
        const seqRef = useRef<HTMLUListElement>(null);

        const [seqWidth, setSeqWidth] = useState<number>(0);
        const [seqHeight, setSeqHeight] = useState<number>(0);
        const [copyCount, setCopyCount] = useState<number>(ANIMATION_CONFIG.MIN_COPIES);
        const [isHovered, setIsHovered] = useState<boolean>(false);

        const effectiveHoverSpeed = useMemo(() => {
            if (hoverSpeed !== undefined) return hoverSpeed;
            if (pauseOnHover === true) return 0;
            if (pauseOnHover === false) return undefined;
            return 0;
        }, [hoverSpeed, pauseOnHover]);

        const isVertical = direction === 'up' || direction === 'down';

        const targetVelocity = useMemo(() => {
            const magnitude = Math.abs(speed);
            let directionMultiplier: number;
            if (isVertical) {
                directionMultiplier = direction === 'up' ? 1 : -1;
            } else {
                directionMultiplier = direction === 'left' ? 1 : -1;
            }
            const speedMultiplier = speed < 0 ? -1 : 1;
            return magnitude * directionMultiplier * speedMultiplier;
        }, [speed, direction, isVertical]);

        const updateDimensions = useCallback(() => {
            const containerWidth = containerRef.current?.clientWidth ?? 0;
            const sequenceRect = seqRef.current?.getBoundingClientRect?.();
            const sequenceWidth = sequenceRect?.width ?? 0;
            const sequenceHeight = sequenceRect?.height ?? 0;
            if (isVertical) {
                const parentHeight = containerRef.current?.parentElement?.clientHeight ?? 0;
                if (containerRef.current && parentHeight > 0) {
                    const targetHeight = Math.ceil(parentHeight);
                    if (containerRef.current.style.height !== `${targetHeight}px`) containerRef.current.style.height = `${targetHeight}px`;
                }
                if (sequenceHeight > 0) {
                    setSeqHeight(Math.ceil(sequenceHeight));
                    const viewport = containerRef.current?.clientHeight ?? parentHeight ?? sequenceHeight;
                    const copiesNeeded = Math.ceil(viewport / sequenceHeight) + ANIMATION_CONFIG.COPY_HEADROOM;
                    setCopyCount(Math.max(ANIMATION_CONFIG.MIN_COPIES, copiesNeeded));
                }
            } else if (sequenceWidth > 0) {
                setSeqWidth(Math.ceil(sequenceWidth));
                const copiesNeeded = Math.ceil(containerWidth / sequenceWidth) + ANIMATION_CONFIG.COPY_HEADROOM;
                setCopyCount(Math.max(ANIMATION_CONFIG.MIN_COPIES, copiesNeeded));
            }
        }, [isVertical]);

        useResizeObserver(updateDimensions, [containerRef, seqRef], [logos, gap, logoHeight, isVertical]);

        useImageLoader(seqRef, updateDimensions, [logos, gap, logoHeight, isVertical]);

        useAnimationLoop(trackRef, targetVelocity, seqWidth, seqHeight, isHovered, effectiveHoverSpeed, isVertical);

        const cssVariables = useMemo(
            () =>
                ({
                    '--logoloop-gap': `${gap}px`,
                    '--logoloop-logoHeight': `${logoHeight}px`,
                    ...(fadeOutColor && { '--logoloop-fadeColor': fadeOutColor }),
                }) as React.CSSProperties,
            [gap, logoHeight, fadeOutColor]
        );

        const rootClasses = useMemo(
            () =>
                cx(
                    'relative group',
                    isVertical ? 'overflow-hidden h-full inline-block' : 'overflow-x-hidden',
                    '[--logoloop-gap:32px]',
                    '[--logoloop-logoHeight:28px]',
                    '[--logoloop-fadeColorAuto:#ffffff]',
                    'dark:[--logoloop-fadeColorAuto:#0b0b0b]',
                    scaleOnHover && 'py-[calc(var(--logoloop-logoHeight)*0.1)]',
                    className
                ),
            [isVertical, scaleOnHover, className]
        );

        const handleMouseEnter = useCallback(() => {
            if (effectiveHoverSpeed !== undefined) setIsHovered(true);
        }, [effectiveHoverSpeed]);
        const handleMouseLeave = useCallback(() => {
            if (effectiveHoverSpeed !== undefined) setIsHovered(false);
        }, [effectiveHoverSpeed]);

        const renderLogoItem = useCallback(
            (item: LogoItem, key: React.Key) => {
                if (renderItem) {
                    return (
                        <li
                            className={cx(
                                'flex-none text-[length:var(--logoloop-logoHeight)] leading-[1]',
                                isVertical ? 'mb-[var(--logoloop-gap)]' : 'mr-[var(--logoloop-gap)]',
                                scaleOnHover && 'overflow-visible group/item'
                            )}
                            key={key}
                            role="listitem"
                        >
                            {renderItem(item, key)}
                        </li>
                    );
                }

                const isNodeItem = 'node' in item;

                const content = isNodeItem ? (
                    <span
                        className={cx(
                            'inline-flex items-center',
                            'motion-reduce:transition-none',
                            scaleOnHover && 'transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover/item:scale-120'
                        )}
                        aria-hidden={!!(item as any).href && !(item as any).ariaLabel}
                    >
                        {(item as any).node}
                    </span>
                ) : (
                    <img
                        className={cx(
                            'h-[var(--logoloop-logoHeight)] w-auto block object-contain',
                            '[-webkit-user-drag:none] pointer-events-none',
                            '[image-rendering:-webkit-optimize-contrast]',
                            'motion-reduce:transition-none',
                            scaleOnHover && 'transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover/item:scale-120',
                            imgClassName
                        )}
                        src={(item as any).src}
                        srcSet={(item as any).srcSet}
                        sizes={(item as any).sizes}
                        width={(item as any).width}
                        height={(item as any).height}
                        alt={(item as any).alt ?? ''}
                        title={(item as any).title}
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                    />
                );

                const itemAriaLabel = isNodeItem ? ((item as any).ariaLabel ?? (item as any).title) : ((item as any).alt ?? (item as any).title);

                const inner = (item as any).href ? (
                    <a
                        className={cx(
                            'inline-flex items-center no-underline rounded',
                            'transition-opacity duration-200 ease-linear',
                            'hover:opacity-80',
                            'focus-visible:outline focus-visible:outline-current focus-visible:outline-offset-2'
                        )}
                        href={(item as any).href}
                        aria-label={itemAriaLabel || 'logo link'}
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        {content}
                    </a>
                ) : (
                    content
                );

                return (
                    <li
                        className={cx(
                            'flex-none text-[length:var(--logoloop-logoHeight)] leading-[1]',
                            isVertical ? 'mb-[var(--logoloop-gap)]' : 'mr-[var(--logoloop-gap)]',
                            scaleOnHover && 'overflow-visible group/item'
                        )}
                        key={key}
                        role="listitem"
                    >
                        {inner}
                    </li>
                );
            },
            [isVertical, scaleOnHover, renderItem, imgClassName]
        );

        const logoLists = useMemo(
            () =>
                Array.from({ length: copyCount }, (_, copyIndex) => (
                    <ul
                        className={cx('flex items-center', isVertical && 'flex-col')}
                        key={`copy-${copyIndex}`}
                        role="list"
                        aria-hidden={copyIndex > 0}
                        ref={copyIndex === 0 ? seqRef : undefined}
                    >
                        {logos.map((item, itemIndex) => renderLogoItem(item, `${copyIndex}-${itemIndex}`))}
                    </ul>
                )),
            [copyCount, logos, renderLogoItem, isVertical]
        );

        const containerStyle = useMemo(
            (): React.CSSProperties => ({
                width: isVertical ? (toCssLength(width) === '100%' ? undefined : toCssLength(width)) : (toCssLength(width) ?? '100%'),
                ...cssVariables,
                ...style,
            }),
            [width, cssVariables, style, isVertical]
        );

        // Force ltr regardless of the page's inherited direction: the
        // offset/margin math above (translate3d, mr-[gap]) assumes an ltr
        // resting layout. Under dir="rtl" the flex track's physical margins
        // and width measurement come out wrong, leaving almost the whole
        // track off-screen — the `direction` prop (not the page's dir) is
        // what actually controls which way logos drift, so forcing ltr here
        // doesn't affect that.
        return (
            <div ref={containerRef} dir="ltr" className={rootClasses} style={containerStyle} role="region" aria-label={ariaLabel}>
                {fadeOut && (
                    <>
                        {isVertical ? (
                            <>
                                <div
                                    aria-hidden
                                    style={{ height: fadeOutWidth }}
                                    className={cx(
                                        'pointer-events-none absolute inset-x-0 top-0 z-10',
                                        'bg-[linear-gradient(to_bottom,var(--logoloop-fadeColor,var(--logoloop-fadeColorAuto))_0%,rgba(0,0,0,0)_100%)]'
                                    )}
                                />
                                <div
                                    aria-hidden
                                    style={{ height: fadeOutWidth }}
                                    className={cx(
                                        'pointer-events-none absolute inset-x-0 bottom-0 z-10',
                                        'bg-[linear-gradient(to_top,var(--logoloop-fadeColor,var(--logoloop-fadeColorAuto))_0%,rgba(0,0,0,0)_100%)]'
                                    )}
                                />
                            </>
                        ) : (
                            <>
                                <div
                                    aria-hidden
                                    style={{ width: fadeOutWidth }}
                                    className={cx(
                                        'pointer-events-none absolute inset-y-0 left-0 z-10',
                                        'bg-[linear-gradient(to_right,var(--logoloop-fadeColor,var(--logoloop-fadeColorAuto))_0%,rgba(0,0,0,0)_100%)]'
                                    )}
                                />
                                <div
                                    aria-hidden
                                    style={{ width: fadeOutWidth }}
                                    className={cx(
                                        'pointer-events-none absolute inset-y-0 right-0 z-10',
                                        'bg-[linear-gradient(to_left,var(--logoloop-fadeColor,var(--logoloop-fadeColorAuto))_0%,rgba(0,0,0,0)_100%)]'
                                    )}
                                />
                            </>
                        )}
                    </>
                )}

                <div
                    className={cx(
                        'flex will-change-transform select-none relative z-0',
                        'motion-reduce:transform-none',
                        isVertical ? 'flex-col h-max w-full' : 'flex-row w-max'
                    )}
                    ref={trackRef}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {logoLists}
                </div>
            </div>
        );
    }
);

LogoLoop.displayName = 'LogoLoop';

export default LogoLoop;
